import {FC} from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from "recharts";
import {
  GARTNER_CHART_COLORS,
  GARTNER_CURRENT_COMPLETED_YEAR,
  GARTNER_HISTOGRAM_FILL_OPACITY,
} from "./constants";
import {IGartnerSeriesChartPoint} from "./utils";

interface IProps {
  title: string;
  yAxisLabel: string;
  data: IGartnerSeriesChartPoint[];
  barColor: string;
  lineColor: string;
}

const SeriesTooltip: FC<TooltipProps<number, string>> = ({
  active,
  payload,
  label,
}) => {
  if (!active || !payload?.length) return null;

  const items = payload.filter((item) => {
    if (item.value === null || item.value === undefined) return false;
    if (
      Number(label) > GARTNER_CURRENT_COMPLETED_YEAR &&
      item.dataKey === "hist"
    ) {
      return false;
    }
    return true;
  });

  if (!items.length) return null;

  return (
    <div
      style={{
        backgroundColor: "rgba(34, 39, 43, 0.95)",
        border: `1px solid ${GARTNER_CHART_COLORS.panelBorder}`,
        borderRadius: 8,
        color: "#fff",
        fontSize: 12,
        padding: "8px 10px",
      }}
    >
      <div style={{marginBottom: 6}}>{`Год: ${label}`}</div>
      {items.map((item) => (
        <div key={item.dataKey} style={{color: item.color}}>
          {`${item.name}: ${item.value}`}
        </div>
      ))}
    </div>
  );
};

export const GartnerSeriesChart: FC<IProps> = ({
  title,
  yAxisLabel,
  data,
  barColor,
  lineColor,
}) => {
  if (!data.length) return null;

  return (
    <div
      style={{
        border: `1px solid ${GARTNER_CHART_COLORS.panelBorder}`,
        borderRadius: "16px",
        background:
          "linear-gradient(180deg, rgba(34, 39, 43, 0.95) 0%, rgba(17, 20, 24, 0.95) 100%)",
        padding: "16px",
        flex: 1,
        minWidth: 0,
      }}
    >
      <div
        style={{
          color: "#FFFFFF",
          fontSize: "15px",
          fontWeight: 600,
          marginBottom: "12px",
        }}
      >
        {title}
      </div>

      <div style={{display: "flex", alignItems: "stretch", gap: 6}}>
        <span
          style={{
            writingMode: "vertical-rl",
            transform: "rotate(180deg)",
            color: GARTNER_CHART_COLORS.axis,
            fontSize: 11,
            lineHeight: 1.2,
            flexShrink: 0,
            alignSelf: "center",
            textAlign: "center",
            maxHeight: 200,
          }}
        >
          {yAxisLabel}
        </span>

        <div style={{flex: 1, minWidth: 0, height: 220}}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={data}
              margin={{top: 8, right: 8, left: 4, bottom: 8}}
            >
              <CartesianGrid
                stroke={GARTNER_CHART_COLORS.grid}
                vertical={false}
              />
              <XAxis
                dataKey="year"
                tick={{fill: GARTNER_CHART_COLORS.axis, fontSize: 10}}
                axisLine={{stroke: GARTNER_CHART_COLORS.grid}}
                tickLine={false}
              />
              <YAxis
                tick={{fill: GARTNER_CHART_COLORS.axis, fontSize: 10}}
                axisLine={false}
                tickLine={false}
                width={48}
              />
              <Tooltip content={<SeriesTooltip />} />
              <Bar
                dataKey="hist"
                fill={barColor}
                fillOpacity={GARTNER_HISTOGRAM_FILL_OPACITY}
                barSize={12}
                name="Факт"
              />
              <Line
                type="monotone"
                dataKey="curve"
                stroke={lineColor}
                dot={false}
                strokeWidth={2.5}
                name="Прогноз"
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};
